import {
  ErrorI,
  isEmail,
  isITPhone,
} from "get-life-storybook-ts/lib/components/Validations";
import { useEffect, useRef, useState } from "react";
import { useDevice } from "get-life-storybook-ts/lib/components/Hooks";
import { posthogCapture, posthogIdentify } from "../../helpers/posthog";
import { pushToDataLayer } from "../../helpers/gtm";
import { getCookie, getReferrerUrl } from "../Hooks";
import prepareCapitals from "../../helpers/Capitals/prepareCapitals";
import { sha256 } from "../../helpers/String/sha256";
import { useTracker } from "../../contexts/TrackingContext";
import {
  buildAppHandoffUrl,
  navigateToApp,
} from "../../helpers/appHandoff";

interface DataTrackerI {
  leadUlid?: string;
  campaign: string;
  utm_campaign?: string | null;
  utm_source?: string | null;
  utm_medium?: string | null;
  tid?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
  utm_test?: string | null;
  gclid?: string | null;
  gbraid?: string | null;
  wbraid?: string | null;
  msclkid?: string | null;
  fbclid?: string | null;
  entryPage?: string | null;
  referrerUrl?: string | null;
  ABTestVersion: string | null;
}

interface FsEventI {
  FsEvent: string;
  data?: {
    leadId?: string;
  };
}

interface IdentifyFSI {
  lead: string;
  data: {
    email: string;
    emailDomain: string;
    brokerId: string;
  };
}
interface ContextProps {
  handleTrack: (value: HandleTrackerI) => void;
  identifyFS: (value: IdentifyFSI) => void;
}

interface HandleTrackerI {
  type: "FS" | "tracker" | "GA";
  data: any;
  event?: string;
}

export interface StrapiDataCalculator {
  data: {
    Url: string;
    Promo?: string;
    backgroundColor?: string;
    HasPhoneValidation?: boolean;
    HasSeriousIllnesses?: boolean;
  }[];
}
interface Validations extends ErrorI<string | number> { }

interface GlobalInfoI {
  defaultcap: string | null;
  gtf_campaign: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
  utm_test?: string | null;
  gclid?: string | null;
  gbraid?: string | null;
  wbraid?: string | null;
  msclkid?: string | null;
  fbclid?: string | null;
  tid?: string | null;
  url: any;
  entryPage?: string | null;
  referrerUrl?: string | null;
  ABTestVersion: string | null;
  ucc?: string | null
}
interface FormDataFirstI {
  birthDate: string;
  capital: number | string;
  gender: string;
  smoke: string;
}

interface FormDataSecondI {
  phone: string;
  email: string;
  checkedConditions: string | boolean;
  checkedConditions3: string | boolean;
  checkedConditions4: string | boolean;
}

const API_URL = process.env.GATSBY_API_URL;

const optionsSmokeInsurance = [
  {
    label: "Sì",
    value: "yes",
  },
  {
    label: "No",
    value: "no",
  },
];

const optionsGenderInsurance = [
  {
    label: "Maschio",
    value: "Male",
  },
  {
    label: "Femmina",
    value: "Female",
  },
];

/**
 * El backend no ha creado el lead (respuesta que no es 2xx ni 409). Se lanza
 * desde signupOrganic para que fetchOrganic no siga: sin lead no hay
 * leadCreated ni handoff a la app, y el usuario ve un error. Mismo arreglo que
 * en el calculador ES tras el incidente del 12-sep-2026.
 */
class LeadSubmissionError extends Error {
  constructor(public readonly status: number) {
    super(`lead submission failed with HTTP ${status}`);
    this.name = "LeadSubmissionError";
  }
}

const errorMessages: any = {
  submission:
    "Non siamo riusciti a calcolare il tuo prezzo in questo momento. Riprova tra qualche secondo o chiamaci.",
  birthDate: "L'età inserita non è assicurabile",
  smoke: "Selezionare una opzione",
  gender: "Selezionare una opzione",
  capital: "Selezionare una capitale",
  phone: "Inserisci un numero di telefono valido",
  email: "Inserire un indirizzo e-mail valido",
  checkedConditions: "Per continuare è necessario che accettiate le condizioni.",
  checkedConditions3: "Per continuare è necessario che accettiate le condizioni.",
  checkedConditions4: "Per continuare è necessario che accettiate le condizioni.",
};

const CalculatorModuleITController = ({ data }: StrapiDataCalculator) => {
  const DOMAIN = process.env.GATSBY_APP_LOCALE;
  const [globalInfo, setGlobalInfo] = useState<GlobalInfoI>({
    defaultcap: null,
    gtf_campaign: data[0]?.Promo || getCookie("gtf_campaign") || null,
    utm_source: null,
    utm_medium: null,
    utm_campaign: null,
    tid: null,
    url: null,
    utm_content: null,
    utm_term: null,
    utm_test: null,
    gclid: null,
    gbraid: null,
    wbraid: null,
    msclkid: null,
    fbclid: null,
    entryPage: null,
    referrerUrl: null,
    ABTestVersion: null,
    ucc: null
  });
  const [formDataFirst, setFormDataFirst] = useState<FormDataFirstI>({
    birthDate: "",
    capital: 0,
    smoke: "",
    gender: "",
  });
  const [formDataSecond, setFormDataSecond] = useState<FormDataSecondI>({
    phone: "",
    email: "",
    checkedConditions: "",
    checkedConditions3: "",
    checkedConditions4: "",
  });
  const [formError, setFormError] = useState<any>();
  const [capitalOptions, setCapitalOptions] = useState<any>([]);
  const [actualStep, setActualStep] = useState(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [display, setDisplay] = useState<boolean>(false);
  const [leadId, setLeadId] = useState<string>("");
  const { isMobile, isDesktop, isTablet } = useDevice();
  const { track } = useTracker();
  const stepStartTime = useRef<number>(Date.now());
  const focusedFields = useRef<Set<string>>(new Set());
  const [cookies, setCookie] = useState("");
  const [optionalCheck, setOptionalCheck] = useState<boolean>(false);



  const handleFocus = (field?: string) => {
    const dataInfo = {
      event: "virtualPageview",
      page: window.location.href + " - " + actualStep,
      pageTitle: `APP Calculator - Paso ${actualStep}`,
      answeredQ: "",
    };

    handleTrack({ type: "GA", data: dataInfo });

    handleTrack({
      type: "FS",
      event: `Calculator Step ${actualStep} ${actualStep === 1 ? "Interacted" : "Viewed"
        }`,
      data: {},
    });

    if (field && !focusedFields.current.has(field)) {
      focusedFields.current.add(field);
      track("field_focused", { field, step: actualStep, calculator: "it" });
    }
  };

  const fieldValidation = async ({ data, key, newError }: any) => {
    if (key !== "redirect") {
      if (!data[key]) {
        newError = {
          ...newError,
          [key]: { error: true, message: errorMessages[key] },
        };
      } else if (key === "phone" && isITPhone({ value: data[key] }).error) {
        newError = {
          ...newError,
          [key]: { error: true, message: errorMessages[key] },
        };
      } else if (key === "email") {
        const validEmail = await validateEmail(data[key]);
        if (!validEmail) {
        newError = {
          ...newError,
          [key]: { error: true, message: errorMessages[key] },
        };
      } else {
        newError = { ...newError, [key]: { error: false, message: "" } };
      }
      } else {
        newError = { ...newError, [key]: { error: false, message: "" } };
      }
    }
    return newError;
  };

  const validateEmail = async (email: string) => {  
    try {
      const response = await fetch(
        `${API_URL}/public/email/validator`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({email: email}),
        }
      )

      if (response.ok) {
        setFormError({ ...formError, email: { error: false, message: "" } });
        return true;
      } else {
        console.warn("⚠️ Validación fallida con status:", response.status);
        setFormError({ ...formError, email: { error: true, message: errorMessages.email } });
        return false;
      }
    } catch (error) {
      return false
    }
  };
  
  const validations = (data: any) => {
    let newError: any = {};
    Object.keys(data).forEach((key: any) => {
      newError = fieldValidation({ data, key, newError });
    });
    let sumValidate = 0;
    Object.keys(newError).forEach((key) => {
      if (!newError[key].error) {
        sumValidate += 1;
      }
    });
    return sumValidate === Object.keys(newError).length ? undefined : newError;
  };

  const signupOrganic = async (dataSecond: any) => {
    let dataOrganic = {
      birthDate: formDataFirst.birthDate,
      // Origin of the lead, like the ES calculator. The promo code travels
      // separately as gtf_campaign; "selectra" (a partner) was wrong here.
      campaign: "calculator-cms",
      capital:
        typeof formDataFirst.capital === "number"
          ? formDataFirst.capital
          : parseInt(formDataFirst.capital),
      initialCapital: typeof formDataFirst.capital === "number"
        ? formDataFirst.capital
        : parseInt(formDataFirst.capital),
      email: dataSecond.email,
      phone: dataSecond.phone,
      nicotine: formDataFirst.smoke,
      gender: formDataFirst.gender,
      userAcceptsCommunications: true
    };

    const response = await fetch(
      `${API_URL}/lead/organic`,
      {
        method: "POST",
        body: JSON.stringify(dataOrganic),
      }
    );
    if (response.status === 409) {
      // Email con lead existente: al login. No hay alta nueva, así que el
      // llamante no debe emitir leadCreated ni navegar a la app.
      track("submission_error", { error_type: "conflict", calculator: "it" });
      navigateToApp(buildAppHandoffUrl(process.env.GATSBY_APP_LOGIN_URL, {}));
      return { handedOff: true as const };
    }
    if (!response.ok) {
      throw new LeadSubmissionError(response.status);
    }
    const organic = await response.json();
    if (!organic?.leadId) {
      throw new LeadSubmissionError(response.status);
    }
    return { handedOff: false as const, ...organic };
  };

  const fetchOrganic = async (dataSecond: any) => {
    setLoading(true);
    try {
      const secondStepErrors = await validations(dataSecond);
      if (secondStepErrors) {
        throw secondStepErrors;
      }

      const organic = await signupOrganic(dataSecond);
      if (organic.handedOff) {
        setLoading(false);
        return;
      }

      // A partir de aquí el lead EXISTE en el backend: solo entonces se
      // identifica, se emite leadCreated y se hace el handoff a la app.
      setLeadId(organic.leadId);

      identifyFS({
        lead: organic.leadId,
        data: {
          emailDomain: dataSecond.email && dataSecond.email.split("@")[1],
          email: dataSecond.email,
          brokerId: null,
        },
      });

      const cleanedPhone = (dataSecond.phone || "").replace(/[\s\-\(\)]/g, "");
      const formattedPhone = cleanedPhone.startsWith("+")
        ? cleanedPhone
        : `+39${cleanedPhone.replace(/^0+/, "")}`;

      const eventData = {
        page: globalInfo.url,
        email: dataSecond.email,
        phone: formattedPhone,
        leadId: organic.leadId,
        calculatedCapital: typeof formDataFirst.capital === "number"
          ? formDataFirst.capital
          : parseInt(formDataFirst.capital),
        utm_campaign: globalInfo.utm_campaign,
        utm_medium: globalInfo.utm_medium,
        utm_source: globalInfo.utm_source,
        utm_content: globalInfo.utm_content,
        utm_term: globalInfo.utm_term,
        utm_test: globalInfo.utm_test,
        gclid: globalInfo.gclid,
        gbraid: globalInfo.gbraid,
        wbraid: globalInfo.wbraid,
        msclkid: globalInfo.msclkid,
        fbclid: globalInfo.fbclid,
        entryPage: globalInfo.entryPage,
        referrerUrl: globalInfo.referrerUrl,
        ABTestVersion: globalInfo.ABTestVersion,
        platform: "App",
      };

      const [hashedEmail, hashedPhone] = await Promise.all([
        sha256(dataSecond.email),
        sha256(formattedPhone),
      ]);

      const leadCreatedEventData = {
        ...eventData,
        email: hashedEmail,
        phone: hashedPhone,
      };

      // Fire tracking events in parallel without blocking navigation
      Promise.all([
        handleTrack({ type: "tracker", data: { leadUlid: organic.leadId, campaign: globalInfo.gtf_campaign || "calculator-cms" } }),
        handleTrack({ type: "FS", event: "leadCreated", data: { ...leadCreatedEventData } }),
        handleTrack({ type: "GA", data: { event: "leadCreated", ...leadCreatedEventData } }),
       ]).catch(() => {});

      // First-party tracking
      track("lead_created", { leadId: organic.leadId, email: dataSecond.email, ...eventData });
      track("cross_domain_redirect", { destination: "app", leadId: organic.leadId }, true);

      if (typeof window !== "undefined") {
        // Cookies do not cross to app.life5.it, so the attribution captured on
        // the marketing site travels in the query string.
        navigateToApp(
          buildAppHandoffUrl(data[0].Url, { lead: organic.leadId })
        );
      }

      setLoading(false);
    } catch (error) {
      if (error instanceof LeadSubmissionError || error instanceof Error) {
        track("submission_error", {
          error_type: error instanceof LeadSubmissionError ? "backend" : "network",
          status: error instanceof LeadSubmissionError ? error.status : undefined,
          calculator: "it",
        });
        setFormError({ ...formError, submission: { error: true, message: errorMessages.submission } });
      } else {
        setFormError(error);
      }
      setLoading(false);
    }
  };

  const handleButton = async (scrollToTop = false) => {
    if (scrollToTop) {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }
    if (actualStep === 1) return await ValidateStepOneAndGoToNextStep();
    if (actualStep === 2) return await ValidateStepTwoAndGoToNextStep();
  };

  const ValidateStepOneAndGoToNextStep = () => {
    const firstStepErrors = validations(formDataFirst);
    if (firstStepErrors) {
      setFormError(firstStepErrors);
    } else {
      track("step_completed", { step: 1, calculator: "it", time_on_step_ms: Date.now() - stepStartTime.current });
      stepStartTime.current = Date.now();
      focusedFields.current.clear();
      setActualStep(2);
    }
  };

  const ValidateStepTwoAndGoToNextStep = async () => {
    let dataSecond = formDataSecond;
    if (formDataSecond.email === "") {
      let newEmail = (document.getElementById("emailInput") as HTMLInputElement)
        .value;
      setFormDataSecond({ ...formDataSecond, email: newEmail });
      dataSecond = { ...formDataSecond, email: newEmail };
    }
    const secondErrors = validations(dataSecond);
    if (secondErrors) return setFormError(secondErrors);

    await fetchOrganic(dataSecond);
  };

  const handleChange = ({ setValue, value, field, formData }: any) => {
    let newData = { ...formData, [field]: value };
    // For email and phone: clear error on change, validate only on blur
    if ((field === "email" || field === "phone") && formError?.[field]?.error) {
      setFormError({ ...formError, [field]: { error: false, message: "" } });
    } else if (value && formError && formError[field] && formError[field].error) {
      // For other fields: validate on change if there was a previous error
      let newError: any = {};
      newError = fieldValidation({ data: newData, key: field, newError });
      setFormError({ ...formError, ...newError });
    }
    setValue(newData);
  };

  const handleBlur = ({ field, formData }: any) => {
    let newError: any = {};
    if (!!formData[field]) {
      newError = fieldValidation({ data: formData, key: field, newError });
      setFormError({ ...formError, ...newError });
      if (newError[field]?.error) {
        track("validation_error", { field, error_type: field === "email" ? "api" : "format", step: actualStep, calculator: "it" });
      } else {
        track("field_completed", { field, step: actualStep, calculator: "it" });
      }
    }
  };

  useEffect(() => {
    if (typeof window !== "undefined" && typeof document !== "undefined") {
      const newQuery = new URLSearchParams(window.location.search);

      setGlobalInfo({
        defaultcap: newQuery.get("defaultcap"),
        gtf_campaign: getCookie("gtf_campaign"),
        utm_source: getCookie("utm_source"),
        utm_medium: getCookie("utm_medium"),
        utm_campaign: getCookie("utm_campaign"),
        tid: getCookie("tid"),
        utm_content: getCookie("utm_content"),
        utm_term: getCookie("utm_term"),
        utm_test: getCookie("utm_test"),
        gclid: getCookie("gclid"),
        gbraid: getCookie("gbraid"),
        wbraid: getCookie("wbraid"),
        msclkid: getCookie("msclkid"),
        fbclid: getCookie("fbclid"),
        entryPage: getCookie("entryPage"),
        referrerUrl: getReferrerUrl(),
        ABTestVersion: getCookie("ABTestVersion"),
        url: window.location.href,
        ucc: newQuery.get("ucc")
      });
    }

    if (actualStep === 2) {
      handleFocus();
    }
  }, [cookies, actualStep]);

  useEffect(() => {
    (async () => {
      if (formDataFirst.birthDate) {
        if (typeof window !== "undefined") {
          try {
            await fetch(
              `${API_URL}/public/age/validation/${formDataFirst.birthDate}`
            ).then((response) => {
              if (response.status === 400) {
                throw new Error;
              } else {
                return response.json()
              }
            })

            const capitals = await fetch(
              `${API_URL}/lead/capital/tarifier/boundaries/${formDataFirst.birthDate}`
            )
              .then((response) => response.json())
              .then((resultData) => {
                return resultData;
              });

            const { options, indexPreSelected } = prepareCapitals({ capitals });

            setCapitalOptions(options);

            if (globalInfo.defaultcap) {
              let capital = options.find(
                (element) => element.value === parseInt(globalInfo.defaultcap!)
              );

              if (capital) {
                setFormDataFirst({
                  ...formDataFirst,
                  capital: capital!.value,
                });
                setFormError({
                  ...formError,
                  birthDate: { error: false, message: "" },
                });
              } else {
                setFormDataFirst({
                  ...formDataFirst,
                  capital: options[indexPreSelected].value,
                });
                setFormError({
                  ...formError,
                  birthDate: { error: false, message: "" },
                });
              }
            } else {
              setFormDataFirst({
                ...formDataFirst,
                capital: options[indexPreSelected].value,
              });
              setFormError({
                ...formError,
                birthDate: { error: false, message: "" },
              });
            }
          } catch (error: any) {
            console.log("error", error);
            if (error.error) {
              setFormError({ ...formError, birthDate: error });
            } else {
              setFormError({
                ...formError,
                birthDate: { error: true, message: errorMessages.birthDate },
              });
            }
            setCapitalOptions([]);
          }
        }
      }
    })();
  }, [formDataFirst.birthDate]);

  useEffect(() => {
    if (typeof document !== "undefined") {
      setCookie(document.cookie);
    }
  }, [actualStep]);

  const isNotDEV = process.env.REACT_APP_ENVIROMENT !== "DEV";
  
  useEffect(() => {
    // `ReactGA.initialize("UA-191842015-1")` used to run here. Universal
    // Analytics was switched off by Google in July 2023, so the only thing it
    // still did was download and execute the dead analytics.js on every
    // calculator page. Removed with the react-ga dependency (2026-09-14); GA4
    // is served by the GTM container.
    track("calculator_viewed", { calculator: "it" });
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      const payload = {
        last_step: actualStep,
        calculator: "it",
        time_on_step_ms: Date.now() - stepStartTime.current,
      };
      if (document.visibilityState === "hidden") {
        track("session_pause", payload, true);
      } else {
        track("session_resume", { last_step: actualStep, calculator: "it" });
      }
    };
    const handlePageHide = () => {
      track("session_end", {
        last_step: actualStep,
        calculator: "it",
        time_on_step_ms: Date.now() - stepStartTime.current,
      }, true);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", handlePageHide);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [actualStep]);

  const VWOID = getCookie("_vwo_uuid_v2");
  const GAID = getCookie("_ga");
  const AT_GD = getCookie("at_gd");
  const API_URL = process.env.GATSBY_API_URL;

  const UTM_CAMPAIGN = getCookie("utm_campaign");
  const UTM_SOURCE = getCookie("utm_source");
  const UTM_MEDIUM = getCookie("utm_medium");
  const UTM_CONTENT = getCookie("utm_content");
  const UTM_TERM = getCookie("utm_term");
  const UTM_TEST = getCookie("utm_test");
  const GCLID = getCookie("gclid");
  const GBRAID = getCookie("gbraid");
  const WBRAID = getCookie("wbraid");
  const MSCLKID = getCookie("msclkid");
  const FBCLID = getCookie("fbclid");
  const TID = getCookie("tid");
  const ENTRYPAGE = getCookie("entryPage");
  const REFERRERURL = getReferrerUrl();
  const ABTESTVERSION = getCookie("ABTestVersion");

  const handleChangeDataTracker = async ({
    leadUlid,
    campaign,
  }: DataTrackerI) => {
    let data = {
      leadUlid: leadUlid,
      googleAnalyticsId: GAID,
      fullstoryId: "",
      vwoId: VWOID,
      utmCampaign: UTM_CAMPAIGN,
      utmSource: UTM_SOURCE,
      utmMedium: UTM_MEDIUM,
      utmContent: UTM_CONTENT,
      utmTerm: UTM_TERM,
      utmTest: UTM_TEST,
      gclid: GCLID,
      gbraid: GBRAID,
      wbraid: WBRAID,
      msclkid: MSCLKID,
      fbclid: FBCLID,
      tid: TID,
      entryPage: ENTRYPAGE,
      referrerUrl: REFERRERURL,
      aBTestVersion: ABTESTVERSION,
      campaign,
      atGd: AT_GD,
    };
    try {
      await fetch(`${API_URL}/lead/track/v2`, {
        method: "POST",
        body: JSON.stringify(data),
      })
        .then((response) => response.json()) // parse JSON from request
        .then((resultData) => {
          return resultData;
        });
    } catch (error) {
      console.log(error);
    }
  };

  const sendFullStoryEvent = async ({ FsEvent, data = {} }: FsEventI) => {
    posthogCapture(FsEvent, data);
    let dataEvent = {
      leadUlid: data.leadId,
      eventType: FsEvent,
      platform: "fullstory",
      createdAt: new Date(),
      payload: data,
    };

    await fetch(`${process.env.GATSBY_APP_EVENTS_API}public/front/event`, {
      method: "POST",
      body: JSON.stringify(dataEvent),
    });
  };

  const handleChangeDataLayer = async (newData: any) => {
    // Was `TagManager.dataLayer({ dataLayer: newData })`: react-gtm-module is a
    // thin wrapper that does exactly this push when window.dataLayer exists,
    // which it always does (seeded inline in <head> by gatsby-ssr.js).
    pushToDataLayer(newData);
    let dataEvent = {
      leadUlid: newData.leadId,
      eventType: newData.event,
      platform: "ga4",
      createdAt: new Date(),
      payload: newData,
    };

    await fetch(`${process.env.GATSBY_APP_EVENTS_API}public/front/event`, {
      method: "POST",
      body: JSON.stringify(dataEvent),
    });
  };

  const identifyFS = ({ lead, data }: IdentifyFSI) => {
    posthogIdentify(
      lead, // Required. Replace 'distinct_id' with your user's unique identifier
      { email: data.email }, // $set, optional
      { data } // $set_once, optional
    );
  };

  const handleTrack = async ({ type, event, data }: HandleTrackerI) => {
    const trackers = {
      FS: () => sendFullStoryEvent({ FsEvent: event!, data }),
      tracker: () => handleChangeDataTracker(data),
      GA: () => handleChangeDataLayer(data),
    };

    return isNotDEV && trackers[type]();
  };

  useEffect(() => {
    const snackbar = document.getElementById("snackbar_module");
    if (snackbar) snackbar.style.display = isMobile ? "none" : "inherit";
  }, [isMobile]);

  const trackStepBack = (from: number, to: number) => {
    track("step_back", { from, to, calculator: "it" });
    stepStartTime.current = Date.now();
    focusedFields.current.clear();
  };

  return {
    actualStep,
    capitalOptions,
    display,
    formDataFirst,
    formDataSecond,
    formError,
    handleBlur,
    handleButton,
    handleChange,
    handleFocus,
    isDesktop,
    isMobile,
    isTablet,
    loading,
    optionsSmokeInsurance,
    optionsGenderInsurance,
    setActualStep,
    setDisplay,
    setFormDataFirst,
    setFormDataSecond,
    optionalCheck,
    setOptionalCheck,
    trackStepBack,
  };
};

export default CalculatorModuleITController;
